import React, { useState, useEffect } from 'react';
import { SmartDevice, DeviceCommand, CommandResult, DeviceStatus } from './interfaces/types';

export interface SmartDeviceControl {
  executeCommand: (deviceId: string, command: DeviceCommand) => Promise<CommandResult>;
  getStatus: (deviceId: string) => Promise<DeviceStatus>;
  getSupportedCommands: (deviceId: string) => DeviceCommand['commandType'][];
}

interface DeviceControlHubProps {
  devices: SmartDevice[];
  onDeviceUpdate: (updatedDevice: SmartDevice) => void;
  onError: (error: string, deviceId: string) => void;
  simulationMode?: boolean;
}

// Create a ref to store the interface instance
let deviceControlInterface: SmartDeviceControl | null = null;

const DeviceControlHub: React.FC<DeviceControlHubProps> = ({ 
  devices, 
  onDeviceUpdate, 
  onError,
  simulationMode = true
}) => {
  const [activeCommands, setActiveCommands] = useState<{[deviceId: string]: string}>({});
  const [commandHistory, setCommandHistory] = useState<{
    deviceId: string;
    command: DeviceCommand;
    timestamp: Date;
    success: boolean;
    message?: string;
  }[]>([]);
  
  // Internal device control functions
  const executeDeviceCommand = async (deviceId: string, command: DeviceCommand): Promise<CommandResult> => {
    try {
      // Find the device
      const device = devices.find(d => d.id === deviceId);
      if (!device) {
        throw new Error(`Device not found: ${deviceId}`);
      }
      
      // Mark command as active
      setActiveCommands(prev => ({ ...prev, [deviceId]: command.commandType }));
      
      // Simulate network latency
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
      
      // If simulation mode, just simulate successful execution with occasional failures
      if (simulationMode) {
        // 10% chance of failure in simulation mode
        const success = Math.random() > 0.1;
        
        if (!success) {
          const errorMessage = `Failed to execute ${command.commandType} on ${device.name}`;
          setCommandHistory(prev => [...prev, {
            deviceId,
            command,
            timestamp: new Date(),
            success: false,
            message: errorMessage
          }]);
          
          // Clear active command
          setActiveCommands(prev => {
            const newState = { ...prev };
            delete newState[deviceId];
            return newState;
          });
          
          onError(errorMessage, deviceId);
          return { success: false, error: errorMessage };
        }
        
        // Update device state based on command
        const newState = calculateNewDeviceState(device.currentState || { isOn: false }, command);
        
        // Update the device
        const updatedDevice = {
          ...device,
          currentState: newState,
          lastSeen: new Date()
        };
        
        onDeviceUpdate(updatedDevice);
        
        // Add to command history
        setCommandHistory(prev => [
          ...prev, 
          {
            deviceId,
            command,
            timestamp: new Date(),
            success: true
          }
        ].slice(-20)); // Keep only the last 20 commands
        
        // Clear active command
        setActiveCommands(prev => {
          const newState = { ...prev };
          delete newState[deviceId];
          return newState;
        });
        
        return { success: true, newState };
      } else {
        // In a real implementation, we'd communicate with actual devices here
        // For now, we'll throw an error since we only support simulation mode
        throw new Error('Real device control not implemented');
      }
    } catch (error) {
      // Clear active command
      setActiveCommands(prev => {
        const newState = { ...prev };
        delete newState[deviceId];
        return newState;
      });
      
      // Handle error
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unknown error executing device command';
      
      // Add to command history
      setCommandHistory(prev => [...prev, {
        deviceId,
        command,
        timestamp: new Date(),
        success: false,
        message: errorMessage
      }]);
      
      onError(errorMessage, deviceId);
      return { success: false, error: errorMessage };
    }
  };
  
  const getDeviceStatus = async (deviceId: string): Promise<DeviceStatus> => {
    try {
      // Find the device
      const device = devices.find(d => d.id === deviceId);
      if (!device) {
        throw new Error(`Device not found: ${deviceId}`);
      }
      
      if (simulationMode) {
        // In simulation mode, just return the current state
        return device.currentState || { isOn: false };
      } else {
        // In a real implementation, we'd query the actual device
        throw new Error('Real device status checking not implemented');
      }
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unknown error getting device status';
      
      onError(errorMessage, deviceId);
      // Return a default "off" state when there's an error
      return { isOn: false };
    }
  };
  
  const getSupportedDeviceCommands = (deviceId: string): DeviceCommand['commandType'][] => {
    // Find the device
    const device = devices.find(d => d.id === deviceId);
    if (!device) {
      return [];
    }
    
    // Return supported commands based on device type
    switch (device.type) {
      case 'LIGHT':
        return ['ON', 'OFF', 'DIM'];
      case 'PLUG':
        return ['ON', 'OFF'];
      case 'BLIND':
        return ['OPEN', 'CLOSE'];
      case 'THERMOSTAT':
        return ['ON', 'OFF', 'SET_TEMPERATURE'];
      case 'SPEAKER':
        return ['ON', 'OFF', 'SET_VOLUME', 'PLAY_MEDIA'];
      case 'TV':
        return ['ON', 'OFF', 'PLAY_MEDIA'];
      case 'COFFEE_MACHINE':
        return ['ON', 'OFF'];
      default:
        return ['ON', 'OFF'];
    }
  };
  
  // Helper to calculate new device state based on command
  const calculateNewDeviceState = (currentState: DeviceStatus, command: DeviceCommand): DeviceStatus => {
    switch (command.commandType) {
      case 'ON':
        return { ...currentState, isOn: true };
      case 'OFF':
        return { ...currentState, isOn: false };
      case 'DIM':
        return { 
          ...currentState, 
          isOn: true, 
          brightness: typeof command.value === 'number' ? command.value : 50 
        };
      case 'SET_TEMPERATURE':
        return { 
          ...currentState, 
          isOn: true, 
          temperature: typeof command.value === 'number' ? command.value : 70 
        };
      case 'SET_VOLUME':
        return { 
          ...currentState, 
          isOn: true, 
          volume: typeof command.value === 'number' ? command.value : 50 
        };
      case 'PLAY_MEDIA':
        return { 
          ...currentState, 
          isOn: true, 
          mode: typeof command.value === 'string' ? command.value : 'play' 
        };
      case 'OPEN':
        return { 
          ...currentState, 
          isOn: true, 
          position: 100 
        };
      case 'CLOSE':
        return { 
          ...currentState, 
          isOn: true, 
          position: 0 
        };
      default:
        return currentState;
    }
  };
  
  // Initialize the interface on component mount
  useEffect(() => {
    // Set up the control interface
    deviceControlInterface = {
      executeCommand: executeDeviceCommand,
      getStatus: getDeviceStatus,
      getSupportedCommands: getSupportedDeviceCommands
    };
    
    // Clean up on unmount
    return () => {
      deviceControlInterface = null;
    };
  }, [devices, simulationMode]); // Re-create when devices or simulation mode changes
  
  // Render component
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium mb-3">Device Control Hub</h3>
      
      <div className="mb-4">
        <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block
          ${simulationMode ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
          {simulationMode ? 'Simulation Mode' : 'Live Mode'}
        </div>
      </div>
      
      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">Connected Devices ({devices.length})</h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {devices.map(device => (
            <div key={device.id} className="border rounded p-2 text-sm flex justify-between items-center">
              <div>
                <div className="font-medium">{device.name}</div>
                <div className="text-xs text-gray-500">{device.type} | {device.location}</div>
              </div>
              <div className="flex items-center">
                {device.status === 'connected' ? (
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-1 ${
                      device.currentState?.isOn ? 'bg-green-500' : 'bg-gray-300'
                    }`}></div>
                    <span className="text-xs">
                      {device.currentState?.isOn ? 'ON' : 'OFF'}
                    </span>
                    {activeCommands[device.id] && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full animate-pulse">
                        {activeCommands[device.id]}...
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-red-500">
                    {device.status === 'pairing' ? 'Pairing...' : 'Disconnected'}
                  </span>
                )}
              </div>
            </div>
          ))}
          
          {devices.length === 0 && (
            <div className="text-sm text-gray-500 p-2 text-center">
              No devices connected
            </div>
          )}
        </div>
      </div>
      
      {commandHistory.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Recent Commands</h4>
          <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
            {commandHistory.slice().reverse().map((cmd, index) => (
              <div key={index} className={`p-1.5 rounded ${
                cmd.success ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {devices.find(d => d.id === cmd.deviceId)?.name || cmd.deviceId}
                  </span>
                  <span className="text-gray-500">
                    {cmd.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex justify-between mt-0.5">
                  <span>
                    {cmd.command.commandType}
                    {cmd.command.value !== undefined ? ` (${cmd.command.value})` : ''}
                  </span>
                  <span className={cmd.success ? 'text-green-600' : 'text-red-600'}>
                    {cmd.success ? 'Success' : 'Failed'}
                  </span>
                </div>
                {!cmd.success && cmd.message && (
                  <div className="text-red-500 mt-0.5">{cmd.message}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Export a function to get the device control interface
export function getDeviceControlInterface(): SmartDeviceControl | null {
  return deviceControlInterface;
}

export default DeviceControlHub; 